/**
 * The assistant screen — a calm, family-safe Claude helper.
 *
 * Two modes: "Ask anything" and "Find a verse". Everyone can use it, kids included, so
 * the copy stays gentle and the answers are kept wholesome by the Worker's system prompt.
 *
 * The page never invents state: it checks whether the assistant is connected first, and
 * renders honest loading / error / not-connected states before it will show an input.
 * A failed connection check is never rendered as an all-clear.
 */
import { useEffect, useState } from 'react';
import { useIsAdult } from '../../lib/identity';
import type { AiConfig } from '../../shared/types';
import { getConfig, explain } from './assistantApi';
import AskPanel from './AskPanel';
import VersePanel from './VersePanel';
import { Button, Note, SparkIcon, cx } from './ui';

type Mode = 'ask' | 'verse';

const MODES: { key: Mode; label: string }[] = [
  { key: 'ask', label: 'Ask anything' },
  { key: 'verse', label: 'Find a verse' },
];

export default function AssistantPage() {
  const isAdult = useIsAdult();
  const [mode, setMode] = useState<Mode>('ask');
  const [config, setConfig] = useState<AiConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getConfig().then(
      (c) => {
        if (cancelled) return;
        setConfig(c);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setError(explain(err, 'Could not reach the assistant.'));
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const configured = config?.configured === true;

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6 sm:px-6">
      <header>
        <div className="flex items-center gap-2 text-honey">
          <SparkIcon />
          <span className="text-xs font-semibold tracking-wide uppercase">Assistant</span>
        </div>
        <h1 className="mt-1 font-display text-2xl font-semibold text-ink">How can I help?</h1>
        <p className="mt-1 text-sm leading-relaxed text-ink-soft">
          A calm helper for questions, ideas, and finding a verse. It can’t see your money,
          calendar, or passwords — only what you type here.
        </p>
      </header>

      <div
        role="group"
        aria-label="Assistant mode"
        className="inline-flex rounded-full border border-border bg-surface p-1"
      >
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            aria-pressed={mode === m.key}
            onClick={() => setMode(m.key)}
            className={cx(
              'min-h-11 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
              mode === m.key ? 'bg-sage text-white' : 'text-ink-soft hover:text-ink'
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      <section className="rounded-card border border-border bg-surface p-5 sm:p-6">
        {loading && (
          <div role="status" aria-live="polite" className="space-y-3">
            <span className="sr-only">Checking the assistant…</span>
            <div className="h-4 w-44 animate-pulse rounded-full bg-surface-sunk" />
            <div className="h-24 w-full animate-pulse rounded-2xl bg-surface-sunk" />
          </div>
        )}

        {!loading && error && (
          <div className="space-y-3">
            <Note tone="warn" role="alert">
              {error}
            </Note>
            <Button type="button" variant="secondary" onClick={() => setNonce((n) => n + 1)}>
              Try again
            </Button>
          </div>
        )}

        {!loading && !error && !configured && (
          <div className="space-y-2 py-2 text-center">
            <p className="font-display text-xl font-semibold text-ink">
              The assistant isn’t connected yet.
            </p>
            <p className="mx-auto max-w-sm text-sm leading-relaxed text-ink-soft">
              {isAdult
                ? 'Add the ANTHROPIC_API_KEY as a Worker secret to switch it on. Until then, it can’t answer.'
                : 'A grown-up needs to switch it on before it can answer. Check back soon!'}
            </p>
          </div>
        )}

        {!loading && !error && configured && (mode === 'ask' ? <AskPanel /> : <VersePanel />)}
      </section>
    </div>
  );
}
